export interface CountryDialCode {
  name: string;
  dial: string;
  iso: string;
}

export const COUNTRY_DIAL_CODES: CountryDialCode[] = [
  { name: 'South Africa', dial: '+27', iso: 'za' },
  { name: 'Namibia', dial: '+264', iso: 'na' },
  { name: 'Botswana', dial: '+267', iso: 'bw' },
  { name: 'Zimbabwe', dial: '+263', iso: 'zw' },
  { name: 'Mozambique', dial: '+258', iso: 'mz' },
  { name: 'Lesotho', dial: '+266', iso: 'ls' },
  { name: 'Eswatini', dial: '+268', iso: 'sz' },
  { name: 'Zambia', dial: '+260', iso: 'zm' },
  { name: 'Kenya', dial: '+254', iso: 'ke' },
  { name: 'Nigeria', dial: '+234', iso: 'ng' },
  { name: 'Ghana', dial: '+233', iso: 'gh' },
  { name: 'United Kingdom', dial: '+44', iso: 'gb' },
  { name: 'Ireland', dial: '+353', iso: 'ie' },
  { name: 'United States / Canada', dial: '+1', iso: 'us' },
  { name: 'Australia', dial: '+61', iso: 'au' },
  { name: 'New Zealand', dial: '+64', iso: 'nz' },
  { name: 'Germany', dial: '+49', iso: 'de' },
  { name: 'France', dial: '+33', iso: 'fr' },
  { name: 'Netherlands', dial: '+31', iso: 'nl' },
  { name: 'Portugal', dial: '+351', iso: 'pt' },
  { name: 'Spain', dial: '+34', iso: 'es' },
  { name: 'Italy', dial: '+39', iso: 'it' },
  { name: 'United Arab Emirates', dial: '+971', iso: 'ae' },
  { name: 'India', dial: '+91', iso: 'in' },
  { name: 'China', dial: '+86', iso: 'cn' },
];

export function countryFlagSrc(iso: string): string {
  return `https://flagcdn.com/24x18/${iso}.png`;
}

/** Default dial for Carmen Lorraine (South Africa). */
export const DEFAULT_COUNTRY_DIAL = '+27';

/**
 * Split a stored phone (often E.164) into dial code + national number for the form.
 * Prefers the longest matching dial from COUNTRY_DIAL_CODES; falls back to +27.
 */
export function splitDialAndNational(
  phone: string,
  preferredDial = DEFAULT_COUNTRY_DIAL,
): { dial: string; national: string } {
  const raw = String(phone || '').trim();
  const preferred = COUNTRY_DIAL_CODES.some((country) => country.dial === preferredDial)
    ? preferredDial
    : DEFAULT_COUNTRY_DIAL;
  if (!raw) return { dial: preferred, national: '' };

  let digitsOnly = raw.replace(/\D/g, '');
  if (raw.startsWith('00')) digitsOnly = digitsOnly.replace(/^00/, '');
  const looksInternational = raw.startsWith('+') || raw.startsWith('00') || digitsOnly.length >= 11;

  if (looksInternational && digitsOnly) {
    const sorted = [...COUNTRY_DIAL_CODES].sort((a, b) => b.dial.length - a.dial.length);
    for (const country of sorted) {
      const dialDigits = country.dial.replace(/\D/g, '');
      if (digitsOnly.startsWith(dialDigits) && digitsOnly.length > dialDigits.length) {
        const rest = digitsOnly.slice(dialDigits.length);
        const national = rest.startsWith('0') ? rest : `0${rest}`;
        return { dial: country.dial, national };
      }
    }
  }

  return { dial: preferred, national: raw };
}

/** Build E.164 (+digits) from dial + national for tel / WhatsApp / API saves. */
export function composePhoneE164(dial: string, national: string): string {
  const nationalDigits = String(national || '').replace(/\D/g, '');
  if (!nationalDigits) return '';
  if (String(national || '').trim().startsWith('+')) {
    return `+${nationalDigits}`;
  }
  const dialDigits = String(dial || DEFAULT_COUNTRY_DIAL).replace(/\D/g, '') || '27';
  return `+${dialDigits}${nationalDigits.replace(/^0+/, '')}`;
}
